import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import * as Showdown from 'showdown';

import {ContentUsage, Dialog} from 'classes/dialog';
import {ModalConfirmationComponent} from 'shared/modals/modal-confirmation/modal-confirmation.component';
import {COURSE_DUMMY, QUESTIONNAIRE_DUMMY, TargetGroup} from 'shared/models/target-groups/target-group';
import {SearchBarComponent} from 'shared/search-bar/search-bar.component';
import {DialogsOverviewService} from './services/dialogs-overview.service';

@Component({
  selector: 'dialogs-overview',
  templateUrl: './dialogs-overview.component.html',
  styleUrls: ['./dialogs-overview.component.css'],
})

export class DialogsOverviewComponent implements OnInit {

  private dialogs: Array<Dialog> = [];
  public fieldsToSearch: Array<Array<string>> = [];
  public filteredDialogs: Array<Dialog>;
  public searchTerm: string;
  private converter: Showdown.Converter = new Showdown.Converter();
  private maxHeightControl: Array<boolean> = [];
  public activeStatus: 'all' | 'active' | 'inactive' = 'all';
  public currentPage: Event;
  public allTargetGroups: Array<TargetGroup> = [];
  public newTargetId: string;
  public deltaDays: string;

  public sortType = null;
  public sortReverse = false;
  public _COURSE_DUMMY = COURSE_DUMMY;
  public _QUESTIONNAIRE_DUMMY = QUESTIONNAIRE_DUMMY;

  @ViewChild('deleteModal') private deleteModal: ModalConfirmationComponent;

  constructor(private dialogsOverviewService: DialogsOverviewService,
              private router: Router) {

    this.dialogs = [];
  }

  public ngOnInit(): void {
    this.dialogsOverviewService.getAllDialogs()
      .subscribe((dialogs: Array<Dialog>) => {
        this.dialogs = dialogs;
        this.filteredDialogs = dialogs;
        this.maxHeightControl.length = this.dialogs.length;
        this.prepareFieldsToSearch();
      });
    this.dialogsOverviewService.getAllTargetGroups()
      .subscribe((allTargetGroups) => {
        this.allTargetGroups = allTargetGroups;
      });
  }

  public gotoCreateDialog(): void { // tslint:disable-line
    this.router.navigate(['dialogs', 'details']);
  }

  private gotoEditDialog(dialogId: number): void { // tslint:disable-line
    this.router.navigate(['dialogs', 'details', {dialogId: dialogId}]);
  }

  public deleteDialog(id: string): void { // tslint:disable-line
    this.dialogsOverviewService.deleteDialog(id)
      .subscribe((response) => {
        const index: number = this.dialogs.findIndex((dialog) => {
          return dialog._id === id;
        });

        if (index > -1) {
          this.dialogs.splice(index, 1);
        }
      });
  }

  public prepareFieldsToSearch(): void {
    this.fieldsToSearch = [];
    this.filteredDialogsByActiveStatus.forEach((dialog) => {
      const fieldsForThatDialog: Array<string> = [
        dialog.title.de.toLowerCase(),
        dialog._id.toLowerCase(),
      ];

      if (dialog.targetGroup !== undefined && dialog.targetGroup !== null) {
        fieldsForThatDialog.push(dialog.targetGroup.title.toLowerCase());
      }

      this.fieldsToSearch.push(fieldsForThatDialog);
      dialog['_title'] = ('' + dialog.title.de).replace('"', '').replace(/^\s+/, '').toLowerCase();
      dialog['_description'] = ('' + dialog.description.de).replace('"', '').replace(/^\s+/, '').toLowerCase();
    });
  }

  public showDeleteModal(dialogId: string): void { // tslint:disable-line
    this.dialogsOverviewService.getContentUsages(dialogId)
    .subscribe((response: Array<ContentUsage>) => {
      if (response.length > 0) {
        alert("Dialog wird noch von Themenpaketen verwendet, z.B. '" + response[0].title['de'] + "'.");
        return;
      }

      const dialogTitle: string = this.getDialogById(dialogId).title.de;
      const content: string = `Sind Sie sicher, dass Sie den Dialog "${dialogTitle}" löschen möchten?`;

      this.deleteModal.setContent(content)
        .setEventData(dialogId)
        .show();
    });
  }

  private getDialogById(id: string): Dialog {
    return this.dialogs.find((dialog) => {
      return dialog._id === id;
    });
  }

  private getActiveIcon(dialog: Dialog): string { // tslint:disable-line
    return dialog.active ? 'glyphicon-ok' : 'glyphicon-remove';
  }

  private getActiveClass(dialog: Dialog): string { // tslint:disable-line
    return dialog.active ? 'no-button-success' : 'no-button-danger';
  }

  public filterDialogs(event: Array<Dialog>): void {
    this.filteredDialogs = this.sortBy(event, this.sortType, this.sortReverse);
  }

  public getLangInfo(dialog: Dialog): string {
    let s = '';
    if ( dialog.title.de ) {
      s = 'de';
    }
    if ( dialog.title.en ) {
      s += (s != '' ? '/' : '') + 'en';
    }
    return s;
  }

  private sortBy(dialogs: Array<Dialog>, prop: string, reverse: boolean): Array<Dialog> {
    if (prop == null) {
      return dialogs;
    }
    const dir = reverse ? -1 : 1;
    return dialogs.sort((a, b) => a[prop] > b[prop] ? dir : a[prop] === b[prop] ? 0 : -dir);
  }

  public get filteredDialogsByActiveStatus(): Array<Dialog> {
    return this.dialogs.filter((dialog) => {
      return this.filterByActiveStatus(dialog);
    });
  }

  public getDialogIntervalText(dialog: Dialog): string {
    let unit: string = '';
    switch (dialog.intervalUnit) {
      case 'DAY': unit = 'Tage'; break;
      case 'WEEK': unit = 'Wochen'; break;
      case 'MONTH': unit = 'Monate'; break;
      case 'YEAR': unit = 'Jahre'; break;
      default: break;
    }

    return `${dialog.intervalValue} ${unit}`;
  }

  private filterByActiveStatus(dialog: Dialog): boolean {
    if (this.activeStatus === 'all') {
      return true;
    }

    const active: boolean = this.activeStatus === 'active';
    return dialog.active === active;
  }

  private getMarkdownDescription(text: string): string {
    return this.converter.makeHtml(text);
  }

  private getMaxHeight(id: number): string {
    if (this.maxHeightControl[id]) {
      return '300px';
    } else {
      return '85px';
    }
  }

  private toggleMaxHeight(id: number): void {
    if (!this.maxHeightControl[id]) {
      this.maxHeightControl[id] = true;
      return;
    }
    this.maxHeightControl[id] = false;
  }

  public cloneDialog(): void {
    const dialogsToClone: Array<String> = [];
    const newTargetId: String = this.newTargetId;

    for (const dialogIndex in this.dialogs) {

      const dialog = this.dialogs[dialogIndex];

      if (dialog.clone === true) {
        dialogsToClone.push(dialog._id.toString());
      }

    }

    this.dialogsOverviewService.cloneDialogs(dialogsToClone, newTargetId, this.deltaDays)
      .subscribe((resultDialogs) => {
        this.dialogs = this.dialogs.concat(resultDialogs);
      });
  }

  public setSort(type) {
    if (this.sortType == type) {
      this.sortReverse = !this.sortReverse
    } else {
      this.sortType = type;
      this.sortReverse = false;
    }
    this.filteredDialogs = this.sortBy(this.filteredDialogs, this.sortType, this.sortReverse);
  }

}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/dialogs/dialogs-overview/dialogs-overview.component.ts